// src/components/StatusBar.js
import React, { useEffect, useState, useContext, useRef } from "react";
import { Box, Typography, IconButton, Tooltip, Paper, ClickAwayListener } from "@mui/material";
import { jwtDecode } from "jwt-decode";
import { useLocation } from 'react-router-dom';
import Cookies from "js-cookie";
import ServerStatus from "./ServerStatus";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useChat } from "./_social/ChatContext";
import { AuthContext } from "./AuthContext";
import { SettingsContext } from "./SettingsContext";

const STATUS_BAR_HEIGHT = 32; // 🎯 Fast høyde på statusbaren

function FormatText({ text }) {
  return (
    <Typography
      sx={{
        color: "#878787",
        fontSize: "0.8rem",
        textAlign: "right",
      }}
    >
      {text}
    </Typography>
  );
}

function StatusBar() {
  const { isChatOpen, setIsChatOpen, isChatLocked, toggleChat } = useChat();
  const { isAuthenticated, role } = useContext(AuthContext);
  const { useSmallSidebar, autoHideSidebar, isSidebarOpen } = useContext(SettingsContext);
  const location = useLocation();
  const [sessionTimeLeft, setSessionTimeLeft] = useState("");
  const [showSessionModal, setShowSessionModal] = useState(false);
  const sessionModalRef = useRef(null);

  // Function to check if current page is public
  const isPublicPage = () => {
    const publicPages = ['/esd-check', '/developer/feedback'];
    return publicPages.includes(location.pathname);
  };

  // Function to check if sidebar should be hidden completely
  const isSidebarHidden = () => {
    const hiddenSidebarPages = ['/esd-check', '/developer/landing', '/developer/feedback'];
    return hiddenSidebarPages.includes(location.pathname);
  };

  const calculateTimeLeft = () => {
    // If not authenticated and on public page, show appropriate message
    if (!isAuthenticated) {
      if (isPublicPage()) {
        setSessionTimeLeft("Not logged in");
      } else {
        setSessionTimeLeft("");
      }
      return;
    }

    const token = Cookies.get("token");
    if (!token) {
      if (isPublicPage()) {
        setSessionTimeLeft("Session expired");
      } else {
        setSessionTimeLeft("Session expired");
      }
      return;
    }

    try {
      const decodedToken = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      const timeLeft = decodedToken.exp - currentTime;

      if (timeLeft <= 0) {
        setSessionTimeLeft("Session expired");
        return;
      }

      const hours = Math.floor(timeLeft / 3600);
      const minutes = Math.floor((timeLeft % 3600) / 60);
      const seconds = Math.floor(timeLeft % 60);
      setSessionTimeLeft(`Session expiring in: ${hours}h ${minutes}m ${seconds}s`);
    } catch (error) {
      console.error("Feil ved dekoding av token:", error);
      setSessionTimeLeft("Error calculating session time");
    }
  };

  useEffect(() => {
    calculateTimeLeft();
    const interval = setInterval(() => {
      calculateTimeLeft();
    }, 1000);
    return () => clearInterval(interval);
  }, [isAuthenticated, location.pathname]);

  useEffect(() => {
    if (!isAuthenticated && isChatOpen) {
      setIsChatOpen(false);
    }
  }, [isAuthenticated, isChatOpen, setIsChatOpen]);

  // Get session expiration color based on time left
  const getSessionColor = () => {
    if (sessionTimeLeft.includes("expired")) return "#ff4747";
    
    // Extract hours from the string if available
    const match = sessionTimeLeft.match(/(\d+)h/);
    if (match) {
      const hours = parseInt(match[1]);
      if (hours < 1) return "#ff9800"; // Less than 1 hour - orange/warning
      return "#878787"; // Normal color
    }
    
    return "#878787"; // Default color
  };

  // Simplified sidebar width calculation - consider completely hidden sidebar
  const sidebarWidth = isSidebarHidden() ? 0 : (autoHideSidebar && !isSidebarOpen ? 0 : (useSmallSidebar ? 120 : 180));

  return (
    <Box
      sx={{
        width: `calc(100% - ${sidebarWidth}px)`,
        bgcolor: "#252526",
        color: "#ffffff",
        textAlign: "left",
        p: 0.5,
        position: "fixed",
        bottom: 0,
        left: sidebarWidth,
        height: `${STATUS_BAR_HEIGHT}px`,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        px: 2,
        transition: theme => theme.transitions.create(['width', 'left'], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.standard,
        }),
        zIndex: 1300,
        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        boxShadow: '0px -2px 4px rgba(0, 0, 0, 0.1)'
      }}
    >
      {/* Venstre side - Server status */}
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {/* Show system status for authenticated users (except workers and repair-cost roles) */}
        {isAuthenticated && role !== 'workers' && role !== 'repair-cost' && (
          <>
            <FormatText text="System status:&nbsp;" />
            <ServerStatus />
          </>
        )}
        {/* Show backend status for workers and repair-cost roles when authenticated */}
        {isAuthenticated && (role === 'workers' || role === 'repair-cost') && (
          <>
            <FormatText text="Backend status:&nbsp;" />
            <ServerStatus showOnlyBackend={true} />
          </>
        )}
        {/* Show backend status for non-authenticated users on public pages */}
        {!isAuthenticated && isPublicPage() && (
          <>
            <FormatText text="Backend status:&nbsp;" />
            <ServerStatus showOnlyBackend={true} />
          </>
        )}
      </Box>

      {/* Høyre side - Chat-knapp + Session info */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        {/* Chat-knapp - skjul for repair-cost rolle */}
        {role !== 'repair-cost' && (
          <Tooltip title={!isAuthenticated ? "Please log in to chat" : (isChatLocked ? "Unlock & close chat" : isChatOpen ? "Close chat" : "Open chat")}>
            <IconButton
              onClick={toggleChat}
              disabled={!isAuthenticated}
              sx={{
                flexShrink: 0,
                color: isChatLocked ? "#ff4747" : isChatOpen ? "#4a9bfc" : "#878787",
                "&:hover": { color: isChatLocked ? "#e33a3a" : "#3580e3" },
                p: 0.5,
              }}
            >
              {isChatOpen ? <ChatBubbleIcon fontSize="small" /> : <ChatBubbleOutlineIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
        
        {/* Session info button - show when authenticated OR on public pages */}
        {(isAuthenticated || isPublicPage()) && (
          <Tooltip title={isAuthenticated ? "View session information" : "View login status"}>
            <IconButton
              size="small"
              onClick={() => setShowSessionModal(true)}
              sx={{
                color: getSessionColor(),
                opacity: 0.8,
                '&:hover': {
                  opacity: 1
                }
              }}
            >
              <AccessTimeIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        
        {/* Session info floating modal */}
        {showSessionModal && (
          <ClickAwayListener onClickAway={() => setShowSessionModal(false)}>
            <Paper
              ref={sessionModalRef}
              elevation={3}
              sx={{
                position: 'fixed',
                bottom: '40px',
                right: '20px',
                padding: '12px 16px',
                width: 'auto',
                minWidth: '220px',
                backgroundColor: '#333',
                color: '#fff',
                borderRadius: '8px',
                zIndex: 1400,
                boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
                animation: 'fadeIn 0.2s ease-in-out',
                '@keyframes fadeIn': {
                  '0%': { opacity: 0, transform: 'translateY(10px)' },
                  '100%': { opacity: 1, transform: 'translateY(0)' }
                }
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                {isAuthenticated ? "Session Status" : "Login Status"}
              </Typography>
              <Typography variant="body2" sx={{ color: getSessionColor() }}>
                {sessionTimeLeft}
              </Typography>
            </Paper>
          </ClickAwayListener>
        )}
      </Box>
    </Box>
  );
}

export { StatusBar, STATUS_BAR_HEIGHT };
export default StatusBar;
